import { ProviderType, PROVIDER_DEFAULTS } from './services/providers/types';

export type AIProviderType = ProviderType | 'gemini';

export interface ProviderSettings {
  type: AIProviderType;
  apiKey?: string;
  baseUrl?: string;
  model: string;
  enabled: boolean;
  cpuOnly?: boolean;
}

export interface AppConfig {
  google: {
    clientId: string;
    scopes: string;
  };
  gemini: {
    apiKey: string;
    textModel: string;
    liveModel: string;
  };
  providers: {
    active: AIProviderType;
    settings: Record<AIProviderType, ProviderSettings>;
  };
  features: {
    enableDriveSync: boolean;
    debugMode: boolean;
  };
  debug: {
    allowedTestUsers: string[];
  };
}

const STORAGE_KEY = 'cc_app_config_v1';

const DEFAULT_PROVIDER_SETTINGS: Record<AIProviderType, ProviderSettings> = {
  selfhosted: {
    type: 'selfhosted',
    apiKey: '',
    baseUrl: 'http://localhost:8800',
    model: 'apriel-1.6-15b-thinker',
    enabled: false
  },
  gemini: {
    type: 'gemini',
    apiKey: '',
    model: 'gemini-2.5-pro',
    enabled: true
  },
  ollama: {
    type: 'ollama',
    baseUrl: 'http://localhost:11434',
    model: 'gemma2:9b',
    enabled: false
  },
  lmstudio: {
    type: 'lmstudio',
    baseUrl: 'http://localhost:1234',
    model: 'local-model',
    enabled: false
  },
  groq: {
    type: 'groq',
    apiKey: '',
    model: 'llama-3.3-70b-versatile',
    enabled: false
  },
  openrouter: {
    type: 'openrouter',
    apiKey: '',
    model: 'anthropic/claude-3.5-sonnet',
    enabled: false
  },
  onnx: {
    type: 'onnx',
    model: 'LiquidAI/LFM2.5-1.2B-Thinking-ONNX',
    enabled: true
  }
};

const GOOGLE_CLIENT_ID = '995655642068-9a3oqofa84f09d7dbijd985knfo6uvqq.apps.googleusercontent.com';

const DEFAULT_CONFIG: AppConfig = {
  google: {
    clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID || GOOGLE_CLIENT_ID,
    scopes: "https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/documents",
  },
  gemini: {
    apiKey: import.meta.env.VITE_GEMINI_API_KEY || '',
    textModel: 'gemini-2.5-pro',
    liveModel: 'gemini-2.5-flash-native-audio-preview-12-2025',
  },
  providers: {
    active: 'gemini',
    settings: DEFAULT_PROVIDER_SETTINGS
  },
  features: {
    enableDriveSync: true,
    debugMode: false,
  },
  debug: {
    allowedTestUsers: (import.meta.env.VITE_DEBUG_TEST_USERS || '').split(',').filter(Boolean),
  }
};

const migrateModelName = (modelName: string): string => {
  const migrations: Record<string, string> = {
    'gemini-2.5-pro-preview-06-05': 'gemini-2.5-pro',
    'gemini-2.5-pro-preview-05-06': 'gemini-2.5-pro',
    'gemini-2.5-flash-preview-04-17': 'gemini-2.5-flash',
    'gemini-2.5-flash-preview-05-20': 'gemini-2.5-flash',
    'gemini-2.5-flash-native-audio-preview-09-2025': 'gemini-2.5-flash-native-audio-preview-12-2025',
  };
  return migrations[modelName] || modelName;
};

export const loadConfig = (): AppConfig => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      // Deep merge with defaults to ensure new keys exist
      // IMPORTANT: Environment variables always take precedence for API keys
      const envApiKey = import.meta.env.VITE_GEMINI_API_KEY || '';
      const envClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || GOOGLE_CLIENT_ID;

      // Migrate old model names to current valid names
      const textModel = migrateModelName(parsed.gemini?.textModel || DEFAULT_CONFIG.gemini.textModel);
      const liveModel = migrateModelName(parsed.gemini?.liveModel || DEFAULT_CONFIG.gemini.liveModel);

      const mergedProviders = {
        active: parsed.providers?.active || DEFAULT_CONFIG.providers.active,
        settings: { ...DEFAULT_PROVIDER_SETTINGS }
      };

      if (parsed.providers?.settings) {
        for (const key of Object.keys(DEFAULT_PROVIDER_SETTINGS) as AIProviderType[]) {
          if (parsed.providers.settings[key]) {
            mergedProviders.settings[key] = {
              ...DEFAULT_PROVIDER_SETTINGS[key],
              ...parsed.providers.settings[key]
            };
          }
        }
      }

      if (envApiKey && mergedProviders.settings.gemini) {
        mergedProviders.settings.gemini.apiKey = envApiKey;
      }

      return {
        ...DEFAULT_CONFIG,
        ...parsed,
        google: {
          ...DEFAULT_CONFIG.google,
          ...parsed.google,
          clientId: envClientId || parsed.google?.clientId || DEFAULT_CONFIG.google.clientId,
        },
        gemini: {
          ...DEFAULT_CONFIG.gemini,
          ...parsed.gemini,
          apiKey: envApiKey || parsed.gemini?.apiKey || DEFAULT_CONFIG.gemini.apiKey,
          textModel,
          liveModel,
        },
        providers: mergedProviders,
        features: { ...DEFAULT_CONFIG.features, ...parsed.features },
        debug: { ...DEFAULT_CONFIG.debug, ...parsed.debug },
      };
    } catch (e) {
      console.warn("Failed to parse config, resetting to defaults");
    }
  }
  return DEFAULT_CONFIG;
};

export const saveConfig = (config: AppConfig, reload: boolean = false) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  // Always update api key in memory immediately
  // This handles the case where user cancels the reload or if reload isn't required
  const newConfig = loadConfig();
  // We need to mutate the exported object if possible, 
  // but since we switched to 'let config', we can reassign it.
  // However, imports in other files might still hold the reference to the old object if they didn't import the binding.
  // ES module exports are live bindings, so 'config' will update.
  config = newConfig;

  if (reload) {
    if (confirm('AI provider settings changed. The app needs to reload to apply these changes. Continue?')) {
      window.location.reload();
    }
  }
};

export let config = loadConfig();

export const isDebugUserAllowed = (userEmail?: string): boolean => {
  if (!userEmail) return false;
  const allowedUsers = config.debug.allowedTestUsers;
  if (allowedUsers.length === 0) return false;
  return allowedUsers.some(email => email.trim().toLowerCase() === userEmail.trim().toLowerCase());
};